// ABOUTME: PWA update provider with silent auto-apply windows and a manual update trigger.
// ABOUTME: The hook lives in useUpdateContext.ts and clearCaches in clearCaches.ts; index.ts re-exports all.

import { useState, useCallback, useEffect, useMemo, useRef, type FC, type PropsWithChildren } from "react"
import * as Sentry from "@sentry/react"
// @ts-expect-error virtual module from vite-plugin-pwa
import { useRegisterSW } from "virtual:pwa-register/react"
import { addLogDebug, addLogError } from "@/model/logger"
import { isUpdateReloadLoop, recordUpdateReload } from "./updateLoopGuard"
import { UpdateContext, useUpdateContext, type UpdateContextType } from "./useUpdateContext"
import config from "../../config"
import { hasUnsavedWork } from "@shared/utils"

// A window (ms) right after a cold start / a return from a long background in
// which a freshly-found update is applied SILENTLY (reload). Outside it, updates
// wait for the sidebar indicator or the next route change (AutoUpdateOnNavigate) —
// i.e. never mid-active-session.
const SILENT_APPLY_WINDOW_MS = 20_000
// Backgrounded at least this long → treat the return as a fresh open.
const LONG_BACKGROUND_MS = 10 * 60 * 1000
// If controllerchange never fires, reload anyway after this long — generous
// enough for the new SW to activate first (the old 2s could reload mid-activation,
// leaving the update pending and re-triggering it on the next cold start = loop).
const CONTROLLERCHANGE_FALLBACK_MS = 10_000

interface UseRegisterSWResult {
  needRefresh: [boolean, (value: boolean) => void]
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>
}

interface UseRegisterSWOptions {
  onRegisteredSW?: (swURL: string, registration: ServiceWorkerRegistration | undefined) => void
  onRegisterError?: (error: Error) => void
}

export const UpdateProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)
  const hiddenSinceRef = useRef<number | null>(null)
  const silentUntilRef = useRef(0)
  // Stable indirection so onRegisteredSW (created before applySilentUpdate) can call it.
  const applyUpdateRef = useRef<() => void>(() => {})

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = (useRegisterSW as (options: UseRegisterSWOptions) => UseRegisterSWResult)({
    onRegisteredSW: (_swURL: string, registration: ServiceWorkerRegistration | undefined) => {
      if (!registration) return
      registrationRef.current = registration

      // Don't arm the silent window (or auto-apply) while we're in a reload
      // loop — an update that isn't sticking must fall back to the manual
      // indicator instead of reloading on every cold start.
      const inReloadLoop = isUpdateReloadLoop()

      if (registration.waiting) {
        // A version downloaded in a previous session is already waiting — apply
        // it now, at cold start, so we open on the latest build. Also arm the
        // silent window as a fallback (the needRefresh effect applies it if the
        // ref isn't wired yet on this first tick).
        if (!inReloadLoop) silentUntilRef.current = Date.now() + SILENT_APPLY_WINDOW_MS
        void applyUpdateRef.current()
      } else if (!inReloadLoop) {
        // Check right at startup (the periodic interval alone can miss a fresh
        // deploy), and silently apply whatever it finds in the opening seconds.
        silentUntilRef.current = Date.now() + SILENT_APPLY_WINDOW_MS
        registration.update().catch((e: unknown) => addLogError("Startup update check failed", e))
      }

      setInterval(
        () => {
          addLogDebug("Checking for app update...")
          registration.update().catch((e: unknown) => {
            addLogError("Error checking for update", e)
          })
        },
        config.CHECK_UPDATE_TIME_IN_MIN * 60 * 1000,
      )
    },
    onRegisterError(error: Error) {
      addLogError("SW registration failed", error)
    },
  })

  const triggerUpdate = useCallback(async () => {
    if (isUpdating) return

    setIsUpdating(true)
    addLogDebug("Applying app update...")
    // Record the reload attempt BEFORE reloading so the loop guard can count it
    // across the reload (all apply paths — silent, on-navigate, manual — count).
    recordUpdateReload()

    try {
      // Listen for the new SW to take control before reloading
      let reloadTriggered = false
      const reloadOnce = () => {
        if (!reloadTriggered) {
          reloadTriggered = true
          window.location.reload()
        }
      }

      // When new SW takes control, reload
      navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, { once: true })

      // Update service worker - it will skipWaiting and activate
      // The new SW has its own precache, so we DON'T clear caches before update
      await updateServiceWorker(true)

      // Fallback: reload only if controllerchange never fires — long enough that
      // the new SW has activated first (see CONTROLLERCHANGE_FALLBACK_MS).
      setTimeout(reloadOnce, CONTROLLERCHANGE_FALLBACK_MS)
    } catch (e: unknown) {
      addLogError("Update failed", e)
      setIsUpdating(false)
      // Fallback: just reload
      window.location.reload()
    }
  }, [isUpdating, updateServiceWorker])

  // Silent auto-apply, but never when the update is looping: if it hasn't stuck
  // after MAX_SILENT_RELOADS, stop reloading and leave it to the sidebar
  // indicator (manual triggerUpdate stays unguarded — the user chose to update).
  const applySilentUpdate = useCallback(() => {
    // Silent means "nothing is lost" — which stops being true the moment a form
    // holds edits. Coming back to the tab after ten minutes with a half-written
    // workout on screen is exactly that case, and the reload took it away. The
    // update is not dropped: needRefresh keeps the sidebar indicator, and
    // AutoUpdateOnNavigate applies it at the next navigation that holds no
    // unsaved work — a message being written in a discussion outlives its screen.
    if (hasUnsavedWork()) {
      addLogDebug("Update ready, but a form has unsaved edits — leaving it to the indicator")
      return
    }
    if (isUpdateReloadLoop()) {
      addLogError("PWA update loop detected — stopping silent auto-apply, falling back to manual indicator")
      Sentry.captureMessage("pwa-update-loop-detected", "warning")
      return
    }
    void triggerUpdate()
  }, [triggerUpdate])

  useEffect(() => {
    applyUpdateRef.current = applySilentUpdate
  }, [applySilentUpdate])

  // Silently apply an update that appears within a cold-start / post-long-
  // background window — the user isn't mid-session then, so nothing is lost.
  // Mid-session updates are left to the sidebar indicator + AutoUpdateOnNavigate.
  useEffect(() => {
    if (needRefresh && Date.now() <= silentUntilRef.current) {
      silentUntilRef.current = 0
      applySilentUpdate()
    }
  }, [needRefresh, applySilentUpdate])

  // Check for updates when the app returns to the foreground; if it was away
  // long enough, treat the return as a fresh open and apply silently.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now()
        return
      }
      const awayMs = hiddenSinceRef.current ? Date.now() - hiddenSinceRef.current : 0
      hiddenSinceRef.current = null

      const registration = registrationRef.current
      if (!registration) return

      if (awayMs >= LONG_BACKGROUND_MS) {
        if (registration.waiting) {
          applySilentUpdate()
          return
        }
        silentUntilRef.current = Date.now() + SILENT_APPLY_WINDOW_MS
      }
      registration.update().catch((e: unknown) => addLogError("Resume update check failed", e))
    }

    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [applySilentUpdate])

  const value = useMemo<UpdateContextType>(
    () => ({
      isUpdateAvailable: needRefresh,
      isUpdating,
      currentVersion: config.BUILD_VERSION,
      triggerUpdate,
    }),
    [needRefresh, isUpdating, triggerUpdate],
  )

  return <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>
}

// Simple version display component (for settings page)
export const VersionDisplay: FC = () => {
  const { currentVersion } = useUpdateContext()
  return <span className="text-xs text-gray-500">v{currentVersion.replace(/-/g, ".")}</span>
}

export default UpdateProvider
